import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import dashboardService from '../../../services/DashboardService.js';
import Button from '../../common/Button.js';
import { formatCurrencyWhole } from '../../../utils/formatCurrency.js';
import Loader from '../../common/Loader.js';
import { formatIstDateTime } from '../../../utils/localDate.js';

const TABS = [
  { id: 'to_hub', labelKey: 'dashboard.toHubSummary' },
  { id: 'route', labelKey: 'dashboard.routeSummary' },
  { id: 'details', labelKey: 'dashboard.details' },
];

const TITLE_KEYS = {
  undispatched: 'dashboard.undispatched',
  in_transit: 'dashboard.inTransit',
  pending_delivery: 'dashboard.pendingDelivery',
  pending_dispatch: 'dashboard.pendingDispatch',
  cargo_missing: 'dashboard.cargoMissing',
};

const PAGE_SIZE = 10;
const TABLE_LINE = 'border border-gray-200';
const TABLE_HEAD_CELL = `px-2 py-2.5 ${TABLE_LINE} font-medium text-gray-600`;
const TABLE_CELL = `px-2 py-2.5 ${TABLE_LINE} text-gray-700`;
const TABLE_CELL_COMPACT = `px-1 py-2 ${TABLE_LINE} text-gray-700`;

const formatBooked = (value) => {
  const formatted = formatIstDateTime(value);
  return formatted === '—' ? '-' : formatted;
};

const HubBadge = ({ show }) => (
  show ? (
    <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">Hub</span>
  ) : null
);

const PaginationBar = ({ page, totalPages, onPageChange, t }) => (
  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 pt-3 border-t border-gray-200">
    <span className="text-sm font-semibold text-gray-700">
      {t('common.page')} {page} {t('common.of')} {totalPages}
    </span>
    <div className="flex items-center gap-2 w-full sm:w-auto">
      <Button
        variant="pagination"
        className="flex-1 sm:flex-none"
        disabled={page <= 1}
        onClick={() => onPageChange(Math.max(1, page - 1))}
      >
        {t('common.previous')}
      </Button>
      <Button
        variant="pagination"
        className="flex-1 sm:flex-none"
        disabled={page >= totalPages}
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
      >
        {t('common.next')}
      </Button>
    </div>
  </div>
);

const CargoDrilldownModal = ({
  open,
  bucket,
  fromHubId,
  hubIds,
  onClose,
  onOpenShipment,
  onOpenMlSheet,
}) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState('to_hub');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    if (!open || !bucket) return undefined;
    setTab('to_hub');
    setPage(1);
    return undefined;
  }, [open, bucket, fromHubId]);

  useEffect(() => {
    setPage(1);
  }, [tab]);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const detailsPage = tab === 'details' ? page : 1;

  useEffect(() => {
    if (!open || !bucket) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const params = { bucket, view: tab };
        if (fromHubId) params.from_hub_id = fromHubId;
        if (hubIds?.length) params.hub_ids = hubIds.join(',');
        if (tab === 'details') {
          params.page = detailsPage;
          params.per_page = PAGE_SIZE;
        }
        const res = await dashboardService.getCargoDrilldown(params);
        if (!cancelled) setPayload(res?.data || null);
      } catch (err) {
        if (!cancelled) setPayload(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, bucket, fromHubId, hubIds, tab, detailsPage]);

  if (!open) return null;

  const totals = payload?.totals || {};
  const rows = payload?.rows || [];
  const totalRecords = tab === 'details'
    ? Number(totals.total || 0)
    : rows.length;
  const totalPages = Math.max(1, Number(totals.pages) || Math.ceil(totalRecords / PAGE_SIZE) || 1);
  const safePage = Math.min(page, totalPages);
  const pagedRows = tab === 'details'
    ? rows
    : rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const title = t(TITLE_KEYS[bucket] || 'dashboard.overviewCargoCount');

  const handleBackdrop = (event) => {
    if (event.target === event.currentTarget) onClose();
  };

  const openShipment = (shipmentId) => {
    if (onOpenShipment) onOpenShipment(shipmentId);
  };

  const openMlSheet = (event, mlSheetId) => {
    if (event) event.stopPropagation();
    if (mlSheetId && onOpenMlSheet) onOpenMlSheet(mlSheetId);
  };

  const mlSheetNo = (row) => row.ml_sheet_id || row.mlSheetId || row.ml_sheet_no || '';

  const mlSheetCell = (row) => {
    const sheetId = mlSheetNo(row);
    return sheetId ? (
      <button
        type="button"
        className="text-blue-600 hover:underline font-normal"
        onClick={(event) => openMlSheet(event, sheetId)}
      >
        {sheetId}
      </button>
    ) : (
      <span className="text-gray-400">-</span>
    );
  };

  const rowAmount = (row) => Number(row?.amount ?? row?.total_amount ?? row?.freight ?? 0);
  const sectionAmount = (() => {
    const reported = Number(totals.amount);
    if (Number.isFinite(reported) && reported > 0) return reported;
    const allRowsVisible = tab !== 'details' || rows.length >= Number(totals.gc_count || totals.total || 0);
    if (allRowsVisible && rows.length) {
      return rows.reduce((sum, row) => sum + rowAmount(row), 0);
    }
    return reported || 0;
  })();
  const lrQtyLabel = (row) => (
    `LR ${row.gc_count} · ${t('dashboard.qty')} ${row.qty} · ${formatCurrencyWhole(row.amount)}`
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-3 sm:p-5 overscroll-contain"
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="bg-white w-full min-w-0 max-h-[calc(100dvh-1.5rem)] sm:max-h-[90vh] rounded-xl sm:w-[calc(100vw-1.5rem)] sm:max-w-[92rem] overflow-hidden flex flex-col border-2 border-blue-200 shadow-lg">
        <div className="flex items-start justify-between gap-3 px-4 sm:px-6 py-4 pt-[max(1rem,env(safe-area-inset-top))] sm:pt-4 border-b border-blue-100">
          <div className="min-w-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800">{title}</h3>
            {payload?.from_hub_name && (
              <p className="text-xs sm:text-sm text-gray-500 truncate mt-0.5">{payload.from_hub_name}</p>
            )}
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <span className="text-[11px] sm:text-sm text-gray-600 whitespace-nowrap">
              {t('dashboard.totalGc')}: <strong>{totals.gc_count || 0}</strong>
            </span>
            <span className="hidden xs:inline text-[11px] sm:text-sm text-gray-600 whitespace-nowrap">
              {t('dashboard.totalQty')}: <strong>{totals.qty || 0}</strong>
            </span>
            <span className="text-[11px] sm:text-sm text-gray-600 whitespace-nowrap">
              {t('dashboard.totalAmount')}: <strong>{formatCurrencyWhole(sectionAmount)}</strong>
            </span>
            <Button variant="ghost" onClick={onClose} className="!p-1.5" aria-label={t('common.close')}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <div className="flex gap-4 sm:gap-6 px-4 sm:px-6 border-b border-blue-100 overflow-x-hidden">
          {TABS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              className={`py-3 text-sm font-medium border-b-2 whitespace-nowrap ${
                tab === item.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t(item.labelKey)}
            </button>
          ))}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 sm:p-6 pb-[max(1rem,env(safe-area-inset-bottom))]">
          {loading ? (
            <Loader fullScreen={false} message={t('common.loading')} />
          ) : tab === 'to_hub' ? (
            <>
              <div className="sm:hidden space-y-2.5">
                {pagedRows.map((row) => (
                  <div
                    key={row.hub_id || row.hub_name}
                    className="w-full p-4 bg-gray-50 rounded-xl border border-blue-100"
                  >
                    <p className="text-sm font-semibold text-gray-800 break-words">
                      {row.hub_name}
                      <HubBadge show={row.is_hub} />
                    </p>
                    <p className="text-xs text-gray-600 mt-1.5">{lrQtyLabel(row)}</p>
                  </div>
                ))}
              </div>
              <div className="hidden sm:block overflow-hidden rounded-lg border border-gray-200">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className={`text-left ${TABLE_HEAD_CELL}`}>{t('dashboard.toHub')}</th>
                      <th className={`text-right ${TABLE_HEAD_CELL}`}>LR</th>
                      <th className={`text-right ${TABLE_HEAD_CELL}`}>{t('dashboard.qty')}</th>
                      <th className={`text-right ${TABLE_HEAD_CELL}`}>{t('dashboard.totalAmount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map((row) => (
                      <tr key={row.hub_id || row.hub_name}>
                        <td className={TABLE_CELL}>
                          {row.hub_name}
                          <HubBadge show={row.is_hub} />
                        </td>
                        <td className={`text-right ${TABLE_CELL}`}>{row.gc_count}</td>
                        <td className={`text-right ${TABLE_CELL}`}>{row.qty}</td>
                        <td className={`text-right tabular-nums ${TABLE_CELL}`}>{formatCurrencyWhole(row.amount)}</td>
                      </tr>
                    ))}
                    {rows.length > 0 && (
                      <tr className="bg-gray-50 font-semibold">
                        <td className={TABLE_CELL}>{t('common.total')}</td>
                        <td className={`text-right ${TABLE_CELL}`}>{totals.gc_count || 0}</td>
                        <td className={`text-right ${TABLE_CELL}`}>{totals.qty || 0}</td>
                        <td className={`text-right tabular-nums ${TABLE_CELL}`}>{formatCurrencyWhole(sectionAmount)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : tab === 'route' ? (
            <>
              <div className="sm:hidden space-y-2.5">
                {pagedRows.map((row, idx) => (
                  <div
                    key={`${row.from_hub_id}-${row.to_hub_id}-${idx}`}
                    className="w-full p-4 bg-gray-50 rounded-xl border border-blue-100"
                  >
                    <p className="text-sm font-semibold text-gray-800 break-words">
                      {(row.from_hub_name || '-')} → {(row.to_hub_name || '-')}
                    </p>
                    <p className="text-xs text-gray-600 mt-1.5">{lrQtyLabel(row)}</p>
                  </div>
                ))}
              </div>
              <div className="hidden sm:block overflow-hidden rounded-lg border border-gray-200">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className={`text-left ${TABLE_HEAD_CELL}`}>{t('dashboard.fromHub')}</th>
                      <th className={`text-left ${TABLE_HEAD_CELL}`}>{t('dashboard.toHub')}</th>
                      <th className={`text-right ${TABLE_HEAD_CELL}`}>LR</th>
                      <th className={`text-right ${TABLE_HEAD_CELL}`}>{t('dashboard.qty')}</th>
                      <th className={`text-right ${TABLE_HEAD_CELL}`}>{t('dashboard.totalAmount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map((row, idx) => (
                      <tr key={`${row.from_hub_id}-${row.to_hub_id}-${idx}`}>
                        <td className={TABLE_CELL}>{row.from_hub_name}</td>
                        <td className={TABLE_CELL}>
                          {row.to_hub_name}
                          <HubBadge show={row.to_is_hub} />
                        </td>
                        <td className={`text-right ${TABLE_CELL}`}>{row.gc_count}</td>
                        <td className={`text-right ${TABLE_CELL}`}>{row.qty}</td>
                        <td className={`text-right tabular-nums ${TABLE_CELL}`}>{formatCurrencyWhole(row.amount)}</td>
                      </tr>
                    ))}
                    {rows.length > 0 && (
                      <tr className="bg-gray-50 font-semibold">
                        <td className={TABLE_CELL} colSpan={2}>{t('common.total')}</td>
                        <td className={`text-right ${TABLE_CELL}`}>{totals.gc_count || 0}</td>
                        <td className={`text-right ${TABLE_CELL}`}>{totals.qty || 0}</td>
                        <td className={`text-right tabular-nums ${TABLE_CELL}`}>{formatCurrencyWhole(sectionAmount)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <>
              <div className="sm:hidden space-y-2.5">
                {pagedRows.map((row) => (
                  <div
                    key={row.shipment_id}
                    className="w-full text-left p-4 bg-gray-50 rounded-xl border border-blue-100"
                  >
                    <button
                      type="button"
                      onClick={() => openShipment(row.shipment_id)}
                      className="text-sm font-normal text-blue-600 break-all hover:underline text-left"
                    >
                      {row.shipment_id}
                    </button>
                    {mlSheetNo(row) ? (
                      <p className="text-xs mt-1">
                        <span className="text-gray-500">{t('dashboard.mlSheetNo', { defaultValue: 'ML Sheet No' })}: </span>
                        <button
                          type="button"
                          className="text-blue-600 hover:underline font-normal"
                          onClick={(event) => openMlSheet(event, mlSheetNo(row))}
                        >
                          {mlSheetNo(row)}
                        </button>
                      </p>
                    ) : (
                      <p className="text-xs text-gray-400 mt-1">
                        {t('dashboard.mlSheetNo', { defaultValue: 'ML Sheet No' })}: -
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1.5">
                      {(row.from_hub_name || '-')} → {(row.to_hub_name || '-')}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      {row.status || '-'} · Qty {row.qty} · {formatCurrencyWhole(rowAmount(row))}
                    </p>
                  </div>
                ))}
                {pagedRows.length > 0 && (
                  <div className="w-full p-3 bg-blue-50 rounded-xl border border-blue-200 text-sm font-semibold text-gray-800 flex justify-between">
                    <span>{t('common.total')}</span>
                    <span className="tabular-nums">{formatCurrencyWhole(sectionAmount)}</span>
                  </div>
                )}
              </div>
              <div className="hidden sm:block w-full min-w-0 overflow-hidden rounded-lg border border-gray-200">
                <table className="w-full min-w-0 text-sm table-fixed border-collapse">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className={`text-left w-[4%] ${TABLE_HEAD_CELL}`}>{t('common.sNo')}</th>
                      <th className={`text-left w-[14%] ${TABLE_HEAD_CELL}`}>GC No.</th>
                      <th className={`text-left w-[13%] ${TABLE_HEAD_CELL}`}>{t('dashboard.mlSheetNo', { defaultValue: 'ML Sheet No' })}</th>
                      <th className={`text-left w-[13%] ${TABLE_HEAD_CELL}`}>{t('dashboard.bookedDate')}</th>
                      <th className={`text-left w-[12%] ${TABLE_HEAD_CELL}`}>{t('common.from')}</th>
                      <th className={`text-left w-[12%] ${TABLE_HEAD_CELL}`}>{t('common.to')}</th>
                      <th className={`text-left w-[7%] px-1 ${TABLE_HEAD_CELL}`}>{t('dashboard.paymentStatus')}</th>
                      <th className={`text-left w-[8%] px-1 ${TABLE_HEAD_CELL}`}>{t('common.status')}</th>
                      <th className={`text-left w-[6%] ${TABLE_HEAD_CELL}`}>{t('dashboard.articles', { defaultValue: 'Article' })}</th>
                      <th className={`text-left w-[5%] ${TABLE_HEAD_CELL}`}>{t('dashboard.qty', { defaultValue: 'Quantity' })}</th>
                      <th className={`text-left w-[6%] ${TABLE_HEAD_CELL}`}>{t('dashboard.totalAmount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map((row, idx) => (
                      <tr
                        key={row.shipment_id}
                        className="cursor-pointer hover:bg-blue-50/50"
                        onClick={() => openShipment(row.shipment_id)}
                      >
                        <td className={TABLE_CELL}>{((totals.page || safePage) - 1) * (totals.per_page || PAGE_SIZE) + idx + 1}</td>
                        <td className={`${TABLE_CELL} break-all`}>
                          <button
                            type="button"
                            className="text-blue-600 hover:underline font-normal"
                            onClick={() => openShipment(row.shipment_id)}
                          >
                            {row.shipment_id}
                          </button>
                        </td>
                        <td className={`${TABLE_CELL} break-all`}>{mlSheetCell(row)}</td>
                        <td className={TABLE_CELL}>{formatBooked(row.booked_date)}</td>
                        <td className={`${TABLE_CELL} break-words`}>{row.from_hub_name || '-'}</td>
                        <td className={`${TABLE_CELL} break-words`}>{row.to_hub_name || '-'}</td>
                        <td className={TABLE_CELL_COMPACT}>
                          <span className="inline-block px-1.5 py-0.5 rounded text-[11px] leading-tight bg-gray-100 text-gray-700 whitespace-nowrap">
                            {row.payment_group === 'to_pay' ? 'ToPay' : row.payment_group === 'on_account' ? 'On Acc' : 'Paid'}
                          </span>
                        </td>
                        <td className={`${TABLE_CELL_COMPACT} break-words text-[13px]`}>{row.status || '-'}</td>
                        <td className={TABLE_CELL}>{row.no_of_articles}</td>
                        <td className={TABLE_CELL}>{row.qty}</td>
                        <td className={`whitespace-nowrap ${TABLE_CELL}`}>{formatCurrencyWhole(rowAmount(row))}</td>
                      </tr>
                    ))}
                    {pagedRows.length > 0 && (
                      <tr className="bg-gray-50 font-semibold">
                        <td className={TABLE_CELL} colSpan={10}>{t('common.total')}</td>
                        <td className={`whitespace-nowrap tabular-nums ${TABLE_CELL}`}>{formatCurrencyWhole(sectionAmount)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {!loading && rows.length === 0 && (
            <p className="text-center text-gray-400 py-8">{t('common.noData')}</p>
          )}
          {!loading && rows.length > 0 && (
            <PaginationBar
              page={safePage}
              totalPages={totalPages}
              onPageChange={setPage}
              t={t}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CargoDrilldownModal;
